import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbejrp14a1h3mr6hp0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    payload.delete("website_alt");

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const serverCode = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && serverCode === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "订阅失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交，请稍后重试。");
    }
  }

  return (
    <section id="subscribe" aria-label="订阅瓜情简报" className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="rounded-lg overflow-hidden border border-background-200 bg-foreground-950">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-6 p-6 md:p-10">
              <p className="text-xs font-label text-accent-400 tracking-widest mb-3">NEWSLETTER</p>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-background-50 leading-tight">
                一条简报，
                <br className="hidden sm:block" />
                把当天的瓜一次讲完
              </h2>
              <p className="mt-4 text-sm text-background-200/80 leading-relaxed max-w-md">
                每个重要瓜情节点，我们发一封简短邮件，只写值得知道的进展与脉络摘要。
                不做无关推送，随时可退订。
              </p>
              <ul className="mt-6 space-y-2.5">
                {["突发事件的首发与后续", "当日热瓜榜与脉络摘要", "深度复盘的完整长文"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-background-100/85">
                    <i className="ri-checkbox-circle-fill text-secondary-400" aria-hidden="true"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-6 bg-background-50 p-6 md:p-10">
              <h3 className="font-heading text-lg font-bold text-foreground-950">订阅瓜情简报</h3>
              <p className="mt-1.5 text-sm text-foreground-600">填写邮箱即可，不需要注册账号。</p>

              {status === "success" ? (
                <div className="mt-6 rounded-md bg-secondary-100 border border-secondary-300 p-5">
                  <p className="flex items-center gap-2 font-heading text-sm font-bold text-secondary-900">
                    <i className="ri-checkbox-circle-fill text-secondary-600" aria-hidden="true"></i>
                    订阅成功
                  </p>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    我们已经收到你的邮箱。下一次重要瓜情更新时，你会第一时间收到简报。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 text-xs font-label text-secondary-800 hover:text-secondary-600 transition-colors cursor-pointer"
                  >
                    再订阅一个邮箱
                  </button>
                </div>
              ) : (
                <form className="mt-6 space-y-4" data-readdy-form onSubmit={handleSubmit} noValidate>
                  <div>
                    <label htmlFor="subscriber-email" className="block text-sm font-label text-foreground-800 mb-1.5">
                      邮箱地址
                    </label>
                    <input
                      id="subscriber-email"
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="subscriber-channel" className="block text-sm font-label text-foreground-800 mb-1.5">
                      你更关注（可选）
                    </label>
                    <select
                      id="subscriber-channel"
                      name="channel_preference"
                      defaultValue=""
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="">全部频道</option>
                      <option value="breaking">突发专报</option>
                      <option value="flash">紧急插播</option>
                      <option value="headline">头条专递</option>
                      <option value="deep">深度瓜情</option>
                    </select>
                  </div>

                  <input
                    className="field-website-alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="rounded-md bg-primary-100 border border-primary-300 px-3.5 py-2.5 text-sm text-primary-800">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-label font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" aria-hidden="true"></i>
                        订阅瓜情简报
                      </>
                    )}
                  </button>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    提交即表示你同意我们仅将邮箱用于发送瓜情简报。我们不会向第三方提供你的邮箱。
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}